import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import { ParsePipelineRepository } from '@telespot/infrastructure/parse';
import { PipelineRepository } from '@telespot/domain';
import { ParseLabelNamesRepository } from '@protocols/infra/parse';
import { type ILabelNamesPort, ResolveLabelNamesQuery, RetrievePipelineStepDetailQuery } from '@protocols/use-cases';
import { rolesStoreProviders } from '@telespot/authentication/ui';

import { switchAlgorithmProviders } from '../../algorithms/switch/switch-algorithm.providers';
import { pipelineCategoryEditorProviders } from '../pipeline-category-editor/pipeline-category-editor.facade.providers';
import {
  PipelineStepEditorEffects,
  RESOLVE_LABEL_NAMES_QUERY,
  RETRIEVE_PIPELINE_STEP_DETAIL_QUERY,
} from '../../../effects/pipeline-step-editor.effects';
import { PipelineTaskSelectionEffects } from '../../../effects/pipeline-task-selection.effects';
import { PipelineStepEditorState } from '../../../state/pipeline-step-editor.state';

import { PipelineStepEditorFacade } from './pipeline-step-editor.facade';

const STEP_EDITOR_PIPELINE_REPOSITORY = new InjectionToken<PipelineRepository>(
  'STEP_EDITOR_PIPELINE_REPOSITORY'
);
const STEP_EDITOR_LABEL_NAMES_PORT = new InjectionToken<ILabelNamesPort>('STEP_EDITOR_LABEL_NAMES_PORT');

/**
 * Production providers required by the Pipeline Step Editor.
 *
 * Spread at the consumer (parent smart component, dialog wrapper, or route
 * wrapper) `providers: []`. The consumer owns the shared editor scope —
 * observing `PipelineStepEditorEffects.confirmed$` (via the facade) is how
 * the parent receives the editor's handoff.
 *
 * Wires Parse-backed repositories and the real use-case handlers, and pulls
 * in `switchAlgorithmProviders` so the embedded `AlgorithmSwitcherComponent`
 * shares the same `AlgorithmSwitcherState` instance the editor effects read
 * on save.
 *
 * The label scope (`LabelEffects`, `LabelState`, `LABEL_REPOSITORY`, label
 * query/command tokens) is intentionally NOT part of this bundle. Production
 * consumers get it from the outer `pipelineEditorCascadeProviders` tier so the
 * step / category / pipeline facades share a single `LabelEffects` instance.
 * Standalone consumers (stories, tests, or any future feature embedding the
 * step editor outside the pipeline editor tree) must spread
 * `labelStoreProviders` themselves.
 *
 * Stories and tests should NOT spread this list — they should declare their
 * own providers with the same Facade/Effects/State classes and mocked
 * use-case implementations.
 */
export const pipelineStepEditorProviders: Provider[] = [
  PipelineStepEditorState,
  ...switchAlgorithmProviders,
  ...pipelineCategoryEditorProviders,
  ...rolesStoreProviders(),
  {
    provide: STEP_EDITOR_PIPELINE_REPOSITORY,
    useFactory: () => new ParsePipelineRepository(Parse, { useMasterKey: false }),
  },
  // This file's own label-name port instance, wired without the master key
  // because the browser has none. Every tier builds its own — including the
  // step-detail tier spread in above — as STEP_EDITOR_PIPELINE_REPOSITORY does;
  // the adapter is stateless, so several is fine. The two consumers declared
  // here resolve through it, so this file wires no `LABEL_REPOSITORY`.
  {
    provide: STEP_EDITOR_LABEL_NAMES_PORT,
    useFactory: () => new ParseLabelNamesRepository(Parse, { useMasterKey: false }),
  },
  {
    provide: RETRIEVE_PIPELINE_STEP_DETAIL_QUERY,
    useFactory: (pipelineRepo: PipelineRepository, labelNamesPort: ILabelNamesPort) =>
      new RetrievePipelineStepDetailQuery(pipelineRepo, labelNamesPort),
    deps: [STEP_EDITOR_PIPELINE_REPOSITORY, STEP_EDITOR_LABEL_NAMES_PORT],
  },
  {
    provide: RESOLVE_LABEL_NAMES_QUERY,
    useFactory: (labelNamesPort: ILabelNamesPort) => new ResolveLabelNamesQuery(labelNamesPort),
    deps: [STEP_EDITOR_LABEL_NAMES_PORT],
  },
  PipelineStepEditorEffects,
  PipelineTaskSelectionEffects,
  PipelineStepEditorFacade,
];
